import { Inject, Injectable } from '@angular/core';
import { EnumTranslator } from '@skylab-angular-shared/core';
import { BadgeTextPipe } from '@skylab-angular-shared/pipes';
import { EnumTranslatorBase, MailTemplateAutotext, MailTemplateKind } from '@skylab-shared/dataModel';
import { Dictionary } from '@skylab-shared/types';

@Injectable({providedIn: 'root'})
export class MailTemplateAutotextService {

    private autotextMap: Record<MailTemplateAutotext, string> = {
        [MailTemplateAutotext.Date]: "@{AktualnaData}",
        [MailTemplateAutotext.Time]: "@{AktualnyCzas}",
        [MailTemplateAutotext.SaleDate]: "@{DataSprzedazy}",
        [MailTemplateAutotext.IssueDate]: "@{DataWystawienia}",
        [MailTemplateAutotext.PaymentSum]: "@{KwotaDoZaplaty}",
        [MailTemplateAutotext.ClientName]: "@{Podmiot_Nazwa}",
        [MailTemplateAutotext.Signature]: "@{Sygnatura}",
        [MailTemplateAutotext.Signatures]: "@{Sygnatury}",
        [MailTemplateAutotext.Currency]: "@{Waluta}",
        [MailTemplateAutotext.CompanyName]: "@{MojaFirma_Nazwa}",
        [MailTemplateAutotext.CurrentUserName]: "@{ZalogowanyUzytkownik_Imie}",
        [MailTemplateAutotext.CurrentUserSurname]: "@{ZalogowanyUzytkownik_Nazwisko}",
        [MailTemplateAutotext.BankAccount]: "@{RachunekBankowy}",
        [MailTemplateAutotext.BankAccountName]: "@{RachunkuBankowy_NazwaRachunku}",
        [MailTemplateAutotext.BankAccountBankName]: "@{RachunkuBankowy_NazwaBanku}",
        [MailTemplateAutotext.BankAccountPln]: "@{RachunekBankowyPLN}",
        [MailTemplateAutotext.BankAccountPlnName]: "@{RachunkuBankowyPLN_NazwaRachunku}",
        [MailTemplateAutotext.BankAccountPlnBankName]: "@{RachunkuBankowyPLN_NazwaBanku}",
        [MailTemplateAutotext.Arrearage]: "@{DniSpoznienia}",
        [MailTemplateAutotext.AccountingFilename]: "@{NazwaPliku}",
        [MailTemplateAutotext.AccountingFileKind]: "@{RodzajPliku}",
        [MailTemplateAutotext.AccountingDateRange]: "@{Okres}"
    };

    private static baseAutotexts = [
        MailTemplateAutotext.Date,
        MailTemplateAutotext.Time,
        MailTemplateAutotext.CompanyName,
        MailTemplateAutotext.CurrentUserName,
        MailTemplateAutotext.CurrentUserSurname
    ];

    private static kindToAutotextsMap: Dictionary<MailTemplateAutotext[]> = {
        [MailTemplateKind.SingleFile]: MailTemplateAutotextService.getAutotextsForDocumentContext(),
        [MailTemplateKind.MultipleFiles]: MailTemplateAutotextService.getAutotextsForDocumentsContext(),
        [MailTemplateKind.UnsettledDocumentReminder]: MailTemplateAutotextService.getAutotextsForUnsettledDocumentReminderContext(),
        [MailTemplateKind.MultipleUnsettledDocumentReminder]: MailTemplateAutotextService.getAutotextsForUnsettledDocumentsReminderContext(),
        [MailTemplateKind.AccountingDataExchangeEmail]: MailTemplateAutotextService.getAutotextsForAccountingDataExchangeContext()
    };

    private autotextTagRegExp = new RegExp(/^@{\w*}/);

    public constructor(
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        private badgeTextPipe: BadgeTextPipe
    ) {}

    public getFormattedContent(mailTemplateKind: MailTemplateKind, omited: MailTemplateAutotext[] = []): string {
        const autotexts = this.getAutotextsForTemplateKind(mailTemplateKind).filter(e => !omited.includes(e))
            .sort((a, b) => Object.keys(this.autotextMap).indexOf(String(a)) - Object.keys(this.autotextMap).indexOf(String(b)));

        return (`
            <ul class="numbering-format__list">
                ${autotexts.map(item => (`<li>${this.badgeTextPipe.transform(`${this.autotextMap[item]} - ${this.enumTranslator.getLabeledEnumValue(MailTemplateAutotext, item)?.label}`, this.autotextTagRegExp)}</li>`)).join('')}
            </ul>
        `);
    }

    private getAutotextsForTemplateKind(kind: MailTemplateKind): MailTemplateAutotext[] {
        return kind in MailTemplateAutotextService.kindToAutotextsMap
            ? MailTemplateAutotextService.kindToAutotextsMap[kind]
            : MailTemplateAutotextService.baseAutotexts;
    }

    private static getAutotextsForDocumentContext(): MailTemplateAutotext[] {
        return [
            ...MailTemplateAutotextService.baseAutotexts,
            MailTemplateAutotext.SaleDate,
            MailTemplateAutotext.IssueDate,
            MailTemplateAutotext.PaymentSum,
            MailTemplateAutotext.ClientName,
            MailTemplateAutotext.Signature,
            MailTemplateAutotext.Currency,
            MailTemplateAutotext.BankAccount,
            MailTemplateAutotext.BankAccountName,
            MailTemplateAutotext.BankAccountBankName,
            MailTemplateAutotext.BankAccountPln,
            MailTemplateAutotext.BankAccountPlnName,
            MailTemplateAutotext.BankAccountPlnBankName
        ];
    }

    private static getAutotextsForDocumentsContext(): MailTemplateAutotext[] {
        return [
            ...MailTemplateAutotextService.baseAutotexts,
            MailTemplateAutotext.Signatures
        ];
    }

    private static getAutotextsForUnsettledDocumentReminderContext(): MailTemplateAutotext[] {
        return [
            ...this.getAutotextsForDocumentContext(),
            MailTemplateAutotext.Arrearage
        ];
    }

    private static getAutotextsForUnsettledDocumentsReminderContext(): MailTemplateAutotext[] {
        return [
            ...this.getAutotextsForDocumentsContext()
        ];
    }

    private static getAutotextsForAccountingDataExchangeContext(): MailTemplateAutotext[] {
        return [
            ...MailTemplateAutotextService.baseAutotexts,
            MailTemplateAutotext.AccountingFileKind,
            MailTemplateAutotext.AccountingDateRange,
            MailTemplateAutotext.AccountingFilename
        ];
    }
}
